import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { CheckCircle2, RefreshCw, Shield, Sparkles } from "lucide-react"
import { useEffect, useMemo, useRef, useState } from "react"

import { Button } from "@/components/button"
import { PageIntroSplitHeader } from "@/components/layout/page-intro-split-header"
import { EmptyState } from "@/components/shared/empty-state"
import { MetricTile } from "@/components/shared/metric-tile"
import { SectionKicker } from "@/components/shared/section-kicker"
import { StatusAlert } from "@/components/shared/status-alert"
import { ToastStack, type AppToast } from "@/components/shared/toast-stack"
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
} from "@/components/ui/card"
import {
  DashboardSkeleton,
  PageHeaderSkeleton,
} from "@/components/ui/page-skeletons"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import {
  talentTrackingApi,
  type TalentProgressBoss,
  type TalentProgressSeason,
  type TalentProgressTalent,
  type TalentTrackingCategory,
} from "@/lib/talent-tracking-api"

const CATEGORY_LABELS: Record<TalentTrackingCategory, string> = {
  offensive: "Offensive",
  defensive: "Defensive",
  strategic: "Strategic",
}

const TALENT_PROGRESS_QUERY_KEY = ["talent-tracking", "me"] as const

type TalentProgressSnapshot = Awaited<
  ReturnType<typeof talentTrackingApi.getMyProgress>
>
type TalentProgressSeasonName = TalentProgressSeason["season"]

type UndoToast = AppToast

const updateBossTalents = (
  boss: TalentProgressBoss,
  shouldUpdate: (talentId: string) => boolean,
  completed: boolean,
) => {
  let changed = false
  const talents = boss.talents.map((talent) => {
    if (!shouldUpdate(talent.id) || talent.completed === completed) {
      return talent
    }

    changed = true
    return {
      ...talent,
      completed,
      missingSince: completed
        ? null
        : (talent.missingSince ?? new Date().toISOString()),
    }
  })

  if (!changed) {
    return boss
  }

  const talentsByCategory = {
    offensive: talents.filter((talent) => talent.category === "offensive"),
    defensive: talents.filter((talent) => talent.category === "defensive"),
    strategic: talents.filter((talent) => talent.category === "strategic"),
  }
  const completedTalentIds = talents
    .filter((talent) => talent.completed)
    .map((talent) => talent.id)
  const missingTalentIds = talents
    .filter((talent) => !talent.completed)
    .map((talent) => talent.id)

  return {
    ...boss,
    talents,
    talentsByCategory,
    completedTalentIds,
    missingTalentIds,
    completedCount: completedTalentIds.length,
    missingCount: missingTalentIds.length,
  }
}

const applyOptimisticUpdate = (
  snapshot: TalentProgressSnapshot,
  shouldUpdate: (talentId: string) => boolean,
  completed: boolean,
) => {
  let didChange = false

  const bosses = snapshot.bosses.map((boss) => {
    const nextBoss = updateBossTalents(boss, shouldUpdate, completed)
    if (nextBoss !== boss) {
      didChange = true
    }
    return nextBoss
  })

  if (!didChange) {
    return snapshot
  }

  const updatedBossesById = new Map(bosses.map((b) => [b.id, b]))

  const seasons = snapshot.seasons.map((season) => {
    const seasonBosses = season.bosses.map((seasonBoss) => {
      return updatedBossesById.get(seasonBoss.id) ?? seasonBoss
    })

    return {
      ...season,
      bosses: seasonBosses,
      completedCount: seasonBosses.reduce(
        (sum, boss) => sum + boss.completedCount,
        0,
      ),
      totalCount: seasonBosses.reduce(
        (sum, boss) => sum + boss.talents.length,
        0,
      ),
    }
  })

  const completedTalentIds = bosses.flatMap((boss) => boss.completedTalentIds)
  const missingTalentIds = bosses.flatMap((boss) => boss.missingTalentIds)

  return {
    ...snapshot,
    bosses,
    seasons,
    bossesById: Object.fromEntries(updatedBossesById),
    completedTalentIds,
    missingTalentIds,
    completionRate:
      completedTalentIds.length + missingTalentIds.length === 0
        ? 0
        : completedTalentIds.length /
          (completedTalentIds.length + missingTalentIds.length),
  }
}

const applyOptimisticTalentToggle = (
  snapshot: TalentProgressSnapshot,
  talentId: string,
  completed: boolean,
) => applyOptimisticUpdate(snapshot, (id) => id === talentId, completed)

const applyOptimisticBulkToggle = (
  snapshot: TalentProgressSnapshot,
  talentIds: string[],
  completed: boolean,
) => {
  const talentIdSet = new Set(talentIds)
  return applyOptimisticUpdate(snapshot, (id) => talentIdSet.has(id), completed)
}

export function TalentProgressDashboard() {
  const queryClient = useQueryClient()
  const [pendingTalentCounts, setPendingTalentCounts] = useState<
    Record<string, number>
  >({})
  const [toasts, setToasts] = useState<UndoToast[]>([])
  const [activeSeason, setActiveSeason] =
    useState<TalentProgressSeasonName | null>(null)
  const toastTimeoutsRef = useRef<
    Record<number, ReturnType<typeof setTimeout>>
  >({})
  const progressQuery = useQuery({
    queryKey: TALENT_PROGRESS_QUERY_KEY,
    queryFn: talentTrackingApi.getMyProgress,
  })

  useEffect(() => {
    const timeoutHandles = toastTimeoutsRef.current

    return () => {
      for (const timeout of Object.values(timeoutHandles)) {
        clearTimeout(timeout)
      }
    }
  }, [])

  const dismissToast = (toastId: number) => {
    const timeout = toastTimeoutsRef.current[toastId]
    if (timeout) {
      clearTimeout(timeout)
      delete toastTimeoutsRef.current[toastId]
    }

    setToasts((current) => current.filter((toast) => toast.id !== toastId))
  }

  const pushToast = (toast: Omit<UndoToast, "id">) => {
    const id = Date.now() + Math.floor(Math.random() * 1000)

    setToasts((current) => [...current, { id, ...toast }])

    toastTimeoutsRef.current[id] = setTimeout(() => {
      dismissToast(id)
    }, 5000)
  }

  const toggleMutation = useMutation({
    mutationFn: async ({
      talentId,
      nextCompleted,
    }: {
      talentId: string
      nextCompleted: boolean
    }) => {
      return nextCompleted
        ? talentTrackingApi.setCompleted(talentId)
        : talentTrackingApi.setIncomplete(talentId)
    },
    onMutate: async ({ talentId, nextCompleted }) => {
      setPendingTalentCounts((current) => ({
        ...current,
        [talentId]: (current[talentId] ?? 0) + 1,
      }))
      await queryClient.cancelQueries({ queryKey: TALENT_PROGRESS_QUERY_KEY })

      const previous = queryClient.getQueryData<TalentProgressSnapshot>(
        TALENT_PROGRESS_QUERY_KEY,
      )

      if (previous) {
        queryClient.setQueryData(
          TALENT_PROGRESS_QUERY_KEY,
          applyOptimisticTalentToggle(previous, talentId, nextCompleted),
        )
      }

      return { previous, talentId }
    },
    onError: (_error, _variables, context) => {
      if (context?.previous) {
        queryClient.setQueryData(TALENT_PROGRESS_QUERY_KEY, context.previous)
      }
    },
    onSuccess: (snapshot) => {
      queryClient.setQueryData(TALENT_PROGRESS_QUERY_KEY, snapshot)
    },
    onSettled: (_data, _error, _variables, context) => {
      if (context?.talentId) {
        setPendingTalentCounts((current) => {
          const nextCount = (current[context.talentId] ?? 1) - 1
          if (nextCount > 0) {
            return {
              ...current,
              [context.talentId]: nextCount,
            }
          }

          const next = { ...current }
          delete next[context.talentId]
          return next
        })
      }
    },
  })

  const bulkToggleMutation = useMutation({
    mutationFn: async ({
      talentIds,
      completed,
    }: {
      talentIds: string[]
      completed: boolean
    }) => {
      return talentTrackingApi.bulkSetStatus(talentIds, completed)
    },
    onMutate: async ({ talentIds, completed }) => {
      setPendingTalentCounts((current) => {
        const next = { ...current }
        for (const talentId of talentIds) {
          next[talentId] = (next[talentId] ?? 0) + 1
        }
        return next
      })
      await queryClient.cancelQueries({ queryKey: TALENT_PROGRESS_QUERY_KEY })

      const previous = queryClient.getQueryData<TalentProgressSnapshot>(
        TALENT_PROGRESS_QUERY_KEY,
      )

      if (previous) {
        queryClient.setQueryData(
          TALENT_PROGRESS_QUERY_KEY,
          applyOptimisticBulkToggle(previous, talentIds, completed),
        )
      }

      return { previous, talentIds }
    },
    onError: (_error, _variables, context) => {
      if (context?.previous) {
        queryClient.setQueryData(TALENT_PROGRESS_QUERY_KEY, context.previous)
      }
    },
    onSuccess: (snapshot) => {
      queryClient.setQueryData(TALENT_PROGRESS_QUERY_KEY, snapshot)
    },
    onSettled: (_data, _error, _variables, context) => {
      if (context?.talentIds) {
        setPendingTalentCounts((current) => {
          const next = { ...current }
          for (const talentId of context.talentIds) {
            const count = (next[talentId] ?? 1) - 1
            if (count > 0) {
              next[talentId] = count
            } else {
              delete next[talentId]
            }
          }
          return next
        })
      }
    },
  })

  const pendingTalentIdSet = useMemo(() => {
    return new Set(Object.keys(pendingTalentCounts))
  }, [pendingTalentCounts])

  const runTalentToggle = async (input: {
    talent: TalentProgressTalent
    nextCompleted: boolean
    suppressUndoToast?: boolean
  }) => {
    try {
      await toggleMutation.mutateAsync({
        talentId: input.talent.id,
        nextCompleted: input.nextCompleted,
      })

      if (!input.suppressUndoToast) {
        pushToast({
          tone: "success",
          text: input.nextCompleted
            ? `Marked tier ${input.talent.tier} complete`
            : `Marked tier ${input.talent.tier} incomplete`,
          actionLabel: "Undo",
          onAction: () => {
            void runTalentToggle({
              talent: {
                ...input.talent,
                completed: input.nextCompleted,
              },
              nextCompleted: !input.nextCompleted,
              suppressUndoToast: true,
            })
          },
        })
      }
    } catch (error) {
      pushToast({
        tone: "error",
        text:
          error instanceof Error
            ? error.message
            : "Failed to update talent progress",
      })
    }
  }

  const runBossBulkToggle = async (input: {
    boss: TalentProgressBoss
    nextCompleted: boolean
  }) => {
    const targetTalents = input.boss.talents.filter(
      (talent) =>
        talent.completed !== input.nextCompleted &&
        !pendingTalentIdSet.has(talent.id),
    )

    if (targetTalents.length === 0) {
      return
    }

    const targetTalentIds = targetTalents.map((talent) => talent.id)

    try {
      await bulkToggleMutation.mutateAsync({
        talentIds: targetTalentIds,
        completed: input.nextCompleted,
      })

      pushToast({
        tone: "success",
        text: input.nextCompleted
          ? `Marked ${targetTalentIds.length} ${targetTalentIds.length === 1 ? "talent" : "talents"} complete for ${input.boss.name}`
          : `Cleared ${targetTalentIds.length} ${targetTalentIds.length === 1 ? "talent" : "talents"} for ${input.boss.name}`,
        actionLabel: "Undo",
        onAction: () => {
          bulkToggleMutation
            .mutateAsync({
              talentIds: targetTalentIds,
              completed: !input.nextCompleted,
            })
            .catch((error) => {
              pushToast({
                tone: "error",
                text:
                  error instanceof Error
                    ? error.message
                    : "Failed to undo bulk update",
              })
            })
        },
      })
    } catch (error) {
      pushToast({
        tone: "error",
        text:
          error instanceof Error
            ? error.message
            : "Failed to bulk update talent status",
      })
    }
  }

  const progress = progressQuery.data
  const seasonTabs: TalentProgressSeasonName[] =
    progress?.seasons.map((season) => season.season) ?? []
  const selectedSeason =
    activeSeason && seasonTabs.includes(activeSeason)
      ? activeSeason
      : (seasonTabs[0] ?? null)

  useEffect(() => {
    if (!selectedSeason) {
      if (activeSeason !== null) {
        setActiveSeason(null)
      }

      return
    }

    if (selectedSeason !== activeSeason) {
      setActiveSeason(selectedSeason)
    }
  }, [activeSeason, selectedSeason])

  if (progressQuery.isLoading) {
    return <DashboardSkeleton cardCount={3} />
  }

  if (progressQuery.isError || !progress) {
    return (
      <div className="grid gap-4">
        <PageHeaderSkeleton />
        <Card>
          <CardContent className="py-6">
            <StatusAlert tone="danger">
              <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <span>
                  {progressQuery.error instanceof Error
                    ? progressQuery.error.message
                    : "Failed to load your talent progress"}
                </span>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={() => void progressQuery.refetch()}
                >
                  <RefreshCw className="size-3.5" />
                  Retry
                </Button>
              </div>
            </StatusAlert>
          </CardContent>
        </Card>
      </div>
    )
  }

  const progressIntroCard = (
    <Card className="page-intro border-primary/10">
      <PageIntroSplitHeader
        kicker={
          <SectionKicker icon={<Sparkles />}>Personal Progress</SectionKicker>
        }
        title="Track the boss talents your character has secured."
        titleClassName="max-w-4xl text-5xl leading-none font-semibold md:text-6xl"
        description="Keep your boss talent record current so your own progression and your guild's planning reflect the same character state."
        descriptionClassName="max-w-2xl text-base leading-7"
        metrics={
          <>
            <MetricTile
              label="Completed"
              value={String(progress.completedTalentIds.length)}
              detail="Boss talents already marked complete on your profile."
            />
            <MetricTile
              label="Missing"
              value={String(progress.missingTalentIds.length)}
              detail="Boss talents still open for progression and future clears."
            />
            <MetricTile
              label="Completion"
              value={`${Math.round(progress.completionRate * 100)}%`}
              detail="Overall progress across the configured seasonal boss talents."
            />
          </>
        }
      />
    </Card>
  )

  return (
    <>
      {selectedSeason ? (
        <Tabs
          value={selectedSeason}
          onValueChange={setActiveSeason}
          className="grid gap-4"
        >
          {progressIntroCard}

          <Card>
            <CardHeader className="gap-4 border-b border-border/50 pb-0">
              <SeasonTabsHeader
                seasons={progress.seasons}
                selectedSeason={selectedSeason}
              />
              <CardDescription className="pb-4">
                {progress.seasons.find(
                  (season) => season.season === selectedSeason,
                )?.completedCount ?? 0}
                /
                {progress.seasons.find(
                  (season) => season.season === selectedSeason,
                )?.totalCount ?? 0}{" "}
                talents completed this season.
              </CardDescription>
            </CardHeader>

            {progress.seasons.map((season) => (
              <TabsContent
                key={season.season}
                value={season.season}
                className="mt-0"
              >
                <CardContent className="pt-6">
                  <SeasonProgressContent
                    season={season}
                    pendingTalentIdSet={pendingTalentIdSet}
                    onBulkToggle={(boss, nextCompleted) => {
                      void runBossBulkToggle({ boss, nextCompleted })
                    }}
                    onToggle={(talent) => {
                      void runTalentToggle({
                        talent,
                        nextCompleted: !talent.completed,
                      })
                    }}
                  />
                </CardContent>
              </TabsContent>
            ))}
          </Card>
        </Tabs>
      ) : (
        <div className="grid gap-4">
          {progressIntroCard}
          <EmptyState
            icon={<Shield className="size-5 text-primary" />}
            title="No boss talents configured"
            description="Your guild hasn't set up any seasonal boss talents yet. Check back later."
          />
        </div>
      )}

      <ToastStack toasts={toasts} dismissToast={dismissToast} />
    </>
  )
}

function SeasonProgressContent({
  season,
  pendingTalentIdSet,
  onBulkToggle,
  onToggle,
}: {
  season: TalentProgressSeason
  pendingTalentIdSet: ReadonlySet<string>
  onBulkToggle: (boss: TalentProgressBoss, nextCompleted: boolean) => void
  onToggle: (talent: TalentProgressTalent) => void
}) {
  if (season.bosses.length === 0) {
    return (
      <EmptyState
        icon={<Shield className="size-5 text-primary" />}
        title="No bosses this season"
        description="No boss talents have been configured for this season."
      />
    )
  }

  return (
    <div className="grid gap-4">
      {season.bosses.map((boss) => (
        <BossProgressCard
          key={boss.id}
          boss={boss}
          pendingTalentIdSet={pendingTalentIdSet}
          onBulkToggle={onBulkToggle}
          onToggle={onToggle}
        />
      ))}
    </div>
  )
}

function SeasonTabsHeader({
  seasons,
  selectedSeason,
}: {
  seasons: TalentProgressSeason[]
  selectedSeason: TalentProgressSeasonName
}) {
  return (
    <TabsList
      variant="line"
      className="h-auto flex-wrap items-center justify-start gap-2 p-0"
    >
      {seasons.map((season, index) => {
        const isActive = season.season === selectedSeason

        return (
          <div key={season.season} className="flex items-center gap-2">
            {index > 0 ? (
              <span className="text-xl font-light text-foreground/28">|</span>
            ) : null}
            <TabsTrigger
              value={season.season}
              className={
                isActive
                  ? "h-auto flex-none items-center gap-2 rounded-none p-0 text-[2rem] leading-none font-semibold text-foreground after:hidden"
                  : "h-auto flex-none items-center gap-2 rounded-none p-0 text-[2rem] leading-none font-semibold text-foreground/45 transition-colors after:hidden hover:text-foreground/72"
              }
            >
              {isActive ? <Shield className="size-5 text-primary" /> : null}
              <span>{season.season}</span>
            </TabsTrigger>
          </div>
        )
      })}
    </TabsList>
  )
}

function BossProgressCard({
  boss,
  pendingTalentIdSet,
  onBulkToggle,
  onToggle,
}: {
  boss: TalentProgressBoss
  pendingTalentIdSet: ReadonlySet<string>
  onBulkToggle: (boss: TalentProgressBoss, nextCompleted: boolean) => void
  onToggle: (talent: TalentProgressTalent) => void
}) {
  const pendingTalentCount = boss.talents.filter((talent) =>
    pendingTalentIdSet.has(talent.id),
  ).length
  const remainingTalents = boss.talents.filter(
    (talent) => !talent.completed && !pendingTalentIdSet.has(talent.id),
  )
  const achievedTalents = boss.talents.filter(
    (talent) => talent.completed && !pendingTalentIdSet.has(talent.id),
  )

  return (
    <div className="grid gap-4 rounded-3xl border border-border/60 bg-card/38 p-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <div className="text-lg font-semibold text-foreground">
            {boss.name}
          </div>
          <div className="mt-1 flex flex-wrap items-center gap-2 text-sm text-muted-foreground">
            <span>
              {boss.completedCount}/{boss.talents.length} completed
            </span>
            {pendingTalentCount > 0 ? (
              <span
                role="status"
                className="text-kicker rounded-full border border-primary/20 bg-primary/8 px-2.5 py-0.5 font-semibold tracking-[0.12em] text-primary"
              >
                Syncing {pendingTalentCount} change
                {pendingTalentCount === 1 ? "" : "s"}...
              </span>
            ) : null}
          </div>
        </div>
        <div className="rounded-full border border-border/70 px-3 py-1 text-xs font-semibold text-muted-foreground">
          {boss.missingCount} remaining
        </div>
      </div>

      <div className="flex flex-wrap gap-2">
        <Button
          type="button"
          size="sm"
          disabled={remainingTalents.length === 0 || pendingTalentCount > 0}
          onClick={() => onBulkToggle(boss, true)}
        >
          Mark remaining complete ({remainingTalents.length})
        </Button>
        <Button
          type="button"
          size="sm"
          variant="outline"
          disabled={achievedTalents.length === 0 || pendingTalentCount > 0}
          onClick={() => onBulkToggle(boss, false)}
        >
          Clear completed ({achievedTalents.length})
        </Button>
      </div>

      <div className="grid gap-4 lg:grid-cols-3">
        {(
          ["offensive", "defensive", "strategic"] as TalentTrackingCategory[]
        ).map((category) => (
          <div
            key={category}
            className="grid gap-3 rounded-[1.2rem] border border-border/50 bg-background/50 p-3"
          >
            <div className="text-kicker text-xs text-muted-foreground">
              {CATEGORY_LABELS[category]}
            </div>
            {boss.talentsByCategory[category].map((talent) => {
              const isPending = pendingTalentIdSet.has(talent.id)

              return (
                <Button
                  unstyled
                  key={talent.id}
                  type="button"
                  aria-pressed={talent.completed}
                  disabled={isPending}
                  onClick={() => onToggle(talent)}
                  className={`inline-card grid gap-2 text-left transition-all ${talent.completed ? "border-success/35 bg-success/10" : "bg-card/65 hover:border-primary/20 hover:bg-card"} ${isPending ? "cursor-wait opacity-75" : ""}`}
                >
                  <div className="flex items-center justify-between gap-3">
                    <div className="text-kicker text-xs text-muted-foreground">
                      Tier {talent.tier}
                    </div>
                    {talent.completed ? (
                      <CheckCircle2 className="size-4 text-success" />
                    ) : null}
                  </div>
                  <div className="line-clamp-2 text-sm/6 font-medium text-foreground">
                    {talent.description}
                  </div>
                  <div className="line-clamp-2 text-xs/5 text-muted-foreground">
                    {talent.reward}
                  </div>
                  <div className="text-xs font-medium text-muted-foreground">
                    {isPending
                      ? "Saving..."
                      : talent.completed
                        ? "Click to mark incomplete"
                        : "Click to mark complete"}
                  </div>
                </Button>
              )
            })}
          </div>
        ))}
      </div>
    </div>
  )
}
